/**
 * DoctorOnboardingWidget (iter100.261) — the always-on trust-progress
 * card shown at the top of the Doctor Dashboard until the doctor is
 * `published` (visible in Find-a-Doctor).
 *
 * Renders one of five states based on lifecycle:
 *   draft       → checklist + progress bar, "Complete X more items"
 *   pending     → "Awaiting verification (typically 1-2 business days)"
 *   verified    → big "Go Live" CTA
 *   published   → collapsed to a small "You're live" chip so it doesn't
 *                 waste dashboard real estate
 *   unpublished → "Currently offline · Go back online" CTA
 *
 * The endpoint /api/doctor/me/onboarding drives everything — one fetch
 * per dashboard load. No polling / no SSE (patient/doctor changes
 * profile → they refresh dashboard → widget updates).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ShieldCheck, ShieldWarning, XCircle,
    Clock, ArrowRight, WarningCircle,
} from "@phosphor-icons/react";

const STATUS_COPY = {
    draft: {
        eyebrow: "Getting ready",
        title: "Complete your profile to get discovered",
        tone: "amber",
        Icon: WarningCircle,
    },
    pending: {
        eyebrow: "Under review",
        title: "Your credentials are being verified",
        subtitle: "This usually takes 1–2 business days. You'll get a notification once done.",
        tone: "sky",
        Icon: Clock,
    },
    verified: {
        eyebrow: "Verified",
        title: "You're verified. Ready to go live?",
        subtitle: "Publishing makes you visible in Find-a-Doctor and lets patients book appointments online.",
        tone: "emerald",
        Icon: ShieldCheck,
    },
    // iter100.79 — `published` intentionally omitted. The widget returns
    // null for published doctors so there's no dashboard chatter for
    // an established doctor.
    unpublished: {
        eyebrow: "Offline",
        title: "You're temporarily offline",
        subtitle: "Patients can't find or book you until you go back online.",
        tone: "slate",
        Icon: ShieldWarning,
    },
};

const TONE_STYLES = {
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    sky:    "bg-sky-50 border-sky-200 text-sky-900",
    emerald:"bg-emerald-50 border-emerald-200 text-emerald-900",
    slate:  "bg-slate-50 border-slate-200 text-slate-900",
};

export default function DoctorOnboardingWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        api.get("/doctor/me/onboarding")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    if (loading || !data) return null;

    const status = data.lifecycle_status || "draft";
    const meta = STATUS_COPY[status] || STATUS_COPY.draft;
    const comp = data.completeness || {};
    // iter100.265 — Draft, pending, and published all render as a single-
    // line chip. The 10-item checklist that used to live here duplicated
    // the completeness banner + section highlights already owned by the
    // /doctor/profile page. Only verified + unpublished render as full
    // cards because they carry dashboard-unique CTAs (Go Live / Go
    // Back Online) that Profile doesn't surface.
    // iter100.79 — The `published` state no longer renders anything.
    // The "You're live" chip was a first-day celebration that we
    // could never reliably scope to "newly published" — the
    // `lifecycle_updated_at` timestamp is stamped on EVERY admin
    // lifecycle transition (verify, publish, unpublish → re-publish),
    // so any admin action within the last 24h re-summoned the chip
    // on established doctors like Dr. Karuna. Retiring the chip is
    // the only fix that guarantees it never shows on a doctor who
    // isn't literally new. Draft / pending still render because they
    // carry actionable state ("finish your profile" / "waiting for
    // admin"). Verified / unpublished still render as full cards
    // because they carry a doctor-only CTA (Go Live / Go Back Online).
    const isChip = status === "draft" || status === "pending";

    // Published doctors get NO onboarding widget at all. Silence is
    // the correct dashboard state for an established doctor.
    if (status === "published") return null;

    const goLive = async () => {
        setBusy(true);
        try {
            await api.post("/doctor/me/publish", {});
            toast.success("You're live 🎉 Patients can now find and book you online.");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Publish failed");
        } finally {
            setBusy(false);
        }
    };

    // iter100.279 — Self-service `Take offline` retired.
    //
    // Industry norm (Zocdoc, Practo) is that a doctor doesn't get a
    // one-click kill switch for their own listing — vacation-mode /
    // "not accepting new patients" toggles are the standard soft
    // controls, and full offline requires admin action so patient
    // continuity and marketplace integrity are preserved.
    //
    // The `POST /doctor/me/unpublish` route still exists on the
    // backend so any legacy `unpublished` doctor can restore
    // themselves via `Go back online`; but no UI surfaces `Take
    // offline` any more. Followup: introduce vacation-mode + soft
    // "not accepting new patients" toggles in `/doctor/settings`
    // once the product decision is finalised.

    const goBackOnline = goLive; // Same endpoint, same result

    // MINI chips (draft, pending) — thin bar. Draft points to
    // /doctor/profile where the real checklist + section-highlights
    // live. Pending is a "waiting for admin" signal. The `published`
    // state is intentionally skipped (see the early return above —
    // iter100.79 retired the "You're live" chip).
    if (isChip) {
        if (status === "draft") {
            return (
                <div
                    className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 flex items-center justify-between gap-3 text-sm"
                    data-testid="doctor-onboarding-draft"
                >
                    <div className="flex items-center gap-2 text-amber-900 min-w-0">
                        <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0" />
                        <span className="font-semibold truncate">
                            Complete your profile to go live · {comp.score ?? 0}% done
                        </span>
                        <span className="text-xs text-amber-800/70 hidden sm:inline">
                            · {comp.passed_count ?? 0}/{comp.total_checks ?? 10} items
                        </span>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => navigate("/doctor/profile")}
                        className="h-7 shrink-0"
                        data-testid="doctor-onboarding-open-profile"
                    >
                        Open Profile <ArrowRight size={12} weight="bold" className="ml-1" />
                    </Button>
                </div>
            );
        }
        // Pending — waiting for admin verification.
        return (
            <div
                className="rounded-xl border border-sky-200 bg-sky-50/60 px-4 py-2.5 flex items-center justify-between gap-3 text-sm"
                data-testid="doctor-onboarding-mini-pending"
            >
                <div className="flex items-center gap-2 min-w-0 text-sky-900">
                    <Clock size={16} weight="fill" className="text-sky-600 shrink-0" />
                    <span className="font-semibold truncate">
                        You&apos;re {comp.score ?? 100}% complete · Waiting for admin review
                    </span>
                    <span className="text-xs hidden sm:inline text-sky-800/70">
                        · Typically 1–2 business days
                    </span>
                </div>
            </div>
        );
    }

    // FULL card (verified + unpublished only — carry dashboard-unique
    // action CTAs not surfaced anywhere else in the doctor portal).
    return (
        <div
            className={`rounded-2xl border p-5 sm:p-6 ${TONE_STYLES[meta.tone]}`}
            data-testid={`doctor-onboarding-${status}`}
        >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <meta.Icon size={18} weight="fill" />
                        <span className="text-[10px] font-bold tracking-[0.18em] uppercase">
                            {meta.eyebrow}
                        </span>
                    </div>
                    <h2 className="font-display text-xl font-bold mt-1">{meta.title}</h2>
                    {meta.subtitle && (
                        <p className="text-sm mt-1 opacity-90">{meta.subtitle}</p>
                    )}
                    {data.rejection_reason && (
                        <div className="mt-3 rounded-lg bg-rose-100 border border-rose-200 p-3 text-sm text-rose-900" data-testid="doctor-onboarding-rejection">
                            <div className="flex items-center gap-1.5 font-semibold mb-1">
                                <XCircle size={14} weight="fill" /> Reason from admin:
                            </div>
                            {data.rejection_reason}
                        </div>
                    )}
                </div>

                {/* Action button per state */}
                {status === "verified" && (
                    <Button onClick={goLive} disabled={busy} size="sm" data-testid="doctor-onboarding-go-live">
                        {busy ? "Going live…" : "Go Live"} <ArrowRight size={14} weight="bold" className="ml-1" />
                    </Button>
                )}
                {status === "unpublished" && (
                    <Button onClick={goBackOnline} disabled={busy} size="sm" data-testid="doctor-onboarding-back-online">
                        {busy ? "…" : "Go back online"} <ArrowRight size={14} weight="bold" className="ml-1" />
                    </Button>
                )}
            </div>
        </div>
    );
}
